import { z } from 'zod';
import { intimacyContextSchema } from '../intimacy/contracts.js';

const part = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), text: z.string() }).strict(),
  z
    .object({
      type: z.literal('image_url'),
      image_url: z
        .object({ url: z.string().min(1), detail: z.enum(['auto', 'low', 'high']).optional() })
        .strict(),
    })
    .strict(),
]);
const message = z
  .object({
    role: z.enum(['system', 'user', 'assistant', 'tool']),
    content: z.union([z.string(), z.array(part).min(1)]),
    tool_call_id: z.string().min(1).optional(),
  })
  .strict();
const tool = z
  .object({
    type: z.literal('function'),
    function: z
      .object({
        name: z.string().min(1),
        description: z.string().optional(),
        parameters: z.record(z.string(), z.unknown()),
      })
      .strict(),
  })
  .strict();
export const MAX_TOKEN_LIMIT = 2_147_483_647;
export const tokenLimitSchema = z.number().int().positive().max(MAX_TOKEN_LIMIT);
const schema = z
  .object({
    model: z.string().trim().min(1).max(200).optional(),
    modelId: z.string().trim().min(1).max(200).optional(),
    messages: z.array(message).min(1).max(1000),
    max_tokens: tokenLimitSchema.optional(),
    maxTokens: tokenLimitSchema.optional(),
    temperature: z.number().min(0).max(2).optional(),
    top_p: z.number().min(0).max(1).optional(),
    stream: z.boolean().default(true),
    reasoning_effort: z.enum(['off', 'low', 'medium', 'high']).optional(),
    tools: z.array(tool).max(100).optional(),
    intimacy: intimacyContextSchema.optional(),
  })
  .strict()
  .superRefine((input, ctx) => {
    if (!(input.modelId ?? input.model))
      ctx.addIssue({ code: 'custom', message: 'MODEL_REQUIRED' });
    if (input.modelId && input.model && input.modelId !== input.model)
      ctx.addIssue({ code: 'custom', message: 'MODEL_ALIAS_CONFLICT' });
    if (
      input.maxTokens !== undefined &&
      input.max_tokens !== undefined &&
      input.maxTokens !== input.max_tokens
    )
      ctx.addIssue({ code: 'custom', message: 'TOKEN_ALIAS_CONFLICT' });
    for (const item of input.messages) {
      if (item.role === 'system' && typeof item.content !== 'string')
        ctx.addIssue({ code: 'custom', message: 'INVALID_SYSTEM_CONTENT' });
      if (item.role === 'tool' && (!item.tool_call_id || typeof item.content !== 'string'))
        ctx.addIssue({ code: 'custom', message: 'INVALID_TOOL_RESULT' });
      if (
        Array.isArray(item.content) &&
        item.role !== 'user' &&
        item.content.some((value) => value.type === 'image_url')
      )
        ctx.addIssue({ code: 'custom', message: 'INVALID_IMAGE_ROLE' });
    }
  });

export function normalizeChatInput(body: unknown) {
  const input = schema.parse(body);
  return {
    modelId: input.modelId ?? input.model!,
    messages: input.messages,
    maxTokens: input.maxTokens ?? input.max_tokens,
    temperature: input.temperature,
    top_p: input.top_p,
    stream: input.stream,
    reasoning_effort: input.reasoning_effort,
    tools: input.tools,
    ...(input.intimacy ? { intimacy: input.intimacy } : {}),
  };
}
export type ChatInput = ReturnType<typeof normalizeChatInput>;
export type ChatMessage = ChatInput['messages'][number];

// Structural mirror of src/core/providers/types.ts; browser runtime modules use
// Vite imports and cannot be pulled into the NodeNext server build unchanged.
export interface GatewayChunk {
  type: 'text' | 'reasoning' | 'tool_call';
  delta: string;
}
